import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { usePageEntryState } from "@/hooks/usePageEntryState";
import { useSlug } from "@/hooks/useSlug";
import { addressMutations } from "@/queries/addresses/mutations";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { routes } from "@/routes";

import { AddressForm, type AddressFormValues, mapAddressFormToRequest } from "../components/AddressForm";

export function EditAddressPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const addressId = id ?? "";
  const buildingQueries = useBuildingQueries();
  const buildingsQuery = useQuery(buildingQueries.list());
  const addressQueries = useAddressQueries();
  const addressesQuery = useQuery(addressQueries.list());
  const addressQuery = useQuery(addressQueries.details(addressId));
  const updateAddress = addressMutations.useUpdateAddress(addressId);
  const refreshLocationCode = addressMutations.useRegenerateLocationCode();
  const address = addressQuery.data;
  const locationCode = address?.locationCode;
  const addressBuilding = address?.building;
  const addressBuildingId = addressBuilding?.id;
  const buildings = buildingsQuery.data?.items ?? [];
  const addresses = addressesQuery.data?.items ?? [];
  const values: AddressFormValues = {
    building:
      buildings.find((building) => building.id === addressBuildingId) ??
      (buildings.length === 1 ? buildings.at(0) : undefined),
    city: address?.city ?? "",
    floor: address?.floor ?? "",
    housenumber: address?.houseNumber ?? "",
    housenumberSuffix: address?.houseNumberSuffix ?? "",
    postalCode: address?.postalCode ?? "",
    streetName: address?.streetName ?? "",
    type: address?.typeConstraint?.key ?? "",
  };
  const form = useForm<AddressFormValues>({
    values,
    resetOptions: { keepDirtyValues: true },
  });
  const pageEntry = usePageEntryState({
    identity: addressId,
    isFetching: addressQuery.isFetching || buildingsQuery.isFetching || addressesQuery.isPending,
    isReady: addressQuery.data !== undefined && buildingsQuery.data !== undefined && addressesQuery.data !== undefined,
    error: addressQuery.error ?? buildingsQuery.error ?? addressesQuery.error,
  });

  if (pageEntry.isPending) {
    return <FullSizeLoader withPadding />;
  }

  if (pageEntry.error) {
    return <ErrorPage error={pageEntry.error} />;
  }

  return (
    <AddressForm
      isEditMode
      addressId={addressId}
      form={form}
      isSubmitting={updateAddress.isPending}
      buildings={buildings}
      addresses={addresses}
      locationCode={locationCode}
      isRefreshingLocationCode={refreshLocationCode.isPending}
      onRefreshLocationCode={() => refreshLocationCode.mutateAsync(addressId)}
      onSubmit={(formValues) => {
        const payload = mapAddressFormToRequest(formValues);
        if (!payload) {
          return;
        }

        updateAddress.mutate(payload, {
          onSuccess: () => {
            void navigate(routes.addresses.list({ slug }));
          },
        });
      }}
    />
  );
}
